import { TMS_LAST_HEARTBEAT_KEY } from '@/constants';
import { userInfoStore } from '@/models';
import { heartbeatApi } from '@/services/api/accounts';
import storage from '@/utils/storage';
import activityTracker from '@/utils/activityTracker';

/**
 * 会话心跳（对应后端"会话滑动续期"机制）：
 * - 每分钟检查一次，满足以下全部条件时调用 /session/heartbeat 续期：
 *   1. 用户未闲置（lastActivity 距今 < 15 分钟）
 *   2. 距上次心跳 > 5 分钟
 *   3. 距上次心跳之后有新的用户操作（lastActivity > lastPing）
 * - 条件 3 是关键：后端过期时间锚定 lastActivity，闲置期间不续命——
 *   用户停止操作后最多再发一次心跳（同步离开前的活跃状态），此后静默，
 *   会话将在 lastActivity + 15 分钟过期，与前端闲置弹窗时机一致
 * - 检查基于时间戳对比（而非定时发送），规避浏览器对后台标签页 setInterval 的节流导致的漏发/误判
 * - lastPing 写入 localStorage 跨标签页共享：任一标签页发过心跳，其他标签页 5 分钟内不再发
 * - 心跳返回 401/403 时由 api 拦截器统一处理（清 token + 跳登录页）
 */
class SessionHeartbeat {
  private timer?: ReturnType<typeof setInterval>;

  /** 每分钟检查间隔 */
  private static readonly CHECK_INTERVAL_MS = 60 * 1000;
  /** 用户闲置判定阈值，与后端会话过期时间一致 */
  private static readonly IDLE_LIMIT_MS = 15 * 60 * 1000;
  /** 后端要求两次心跳最小间隔 5 分钟 */
  private static readonly PING_MIN_GAP_MS = 5 * 60 * 1000;

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), SessionHeartbeat.CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    // 启动时先补判一次（如上次心跳已超过 5 分钟）
    this.tick();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /** 执行一次心跳条件判断；也供调试面板手动触发 */
  tick(): void {
    // 组织未选择（如登录页选组织阶段）：请求不带 OrganizationId 头，
    // 后端会拒绝该心跳并触发 401 跳转；此时会话刚由 authenticate 建立，
    // 无需续期，闲置检测（弹窗）不受影响
    if (!userInfoStore.organizationId) return;
    const lastActivity = activityTracker.getLastActivity();
    if (!lastActivity) return;
    // 已闲置：不续期，等闲置弹窗的重登/退出流程接管
    if (Date.now() - lastActivity >= SessionHeartbeat.IDLE_LIMIT_MS) return;
    const lastPing = Number(storage.localGet(TMS_LAST_HEARTBEAT_KEY)) || 0;
    // 其他标签页刚发过（跨标签页共享 lastPing）
    if (Date.now() - lastPing < SessionHeartbeat.PING_MIN_GAP_MS) return;
    // 上次心跳之后没有新的用户操作：不发（后端锚定 lastActivity 过期，闲置期间不续命）
    if (lastActivity <= lastPing) return;
    this.ping();
  }

  private handleVisibilityChange = (): void => {
    // 标签页回到前台立即补判一次（后台期间 setInterval 可能被节流）
    if (!document.hidden) this.tick();
  };

  private async ping(): Promise<void> {
    // 先记时间再发请求，防止并发重复发送；失败不重试，等下一轮 tick
    storage.localSet(TMS_LAST_HEARTBEAT_KEY, String(Date.now()));
    try {
      await heartbeatApi();
    } catch {
      // 401/403 由 api 拦截器统一处理；网络错误静默，下一轮重试
    }
  }
}

export default new SessionHeartbeat();
